import { UrlProviderLocation } from '../../types'

const parseS3BucketAndKey = (url: string) => {
  const s3Url = new URL(url)

  // support both s3 URL types
  // e.g. https://s3.us-west-2.amazonaws.com/arpeggi-prod-private/rvc/user_uploaded_index_files/a.index
  // or https://arpeggi-staging-private.s3.us-west-2.amazonaws.com/rvc/db-storage/a.index
  let bucket = ''
  let key = ''
  const urlHeader = s3Url.hostname.split('.')[0]
  const path = s3Url.pathname.slice(1).split('?')[0]
  if (urlHeader === 's3') {
    bucket = path.split('/')[0]
    key = path.split('/').slice(1).join('/')
  } else {
    bucket = urlHeader
    key = path
  }

  if (!key || !bucket) {
    throw new Error(`Invalid S3 url: ${url}`)
  }

  // to prevent double encoding spaces
  key = key.replace('%20', ' ')
  return { bucket, key }
}

const parseR2BucketAndKey = (url: string) => {
  // R2 url format:
  // https://<acount_id>.r2.cloudflarestorage.com/<bucket>/<key>
  const path = url.split('cloudflarestorage.com/')[1].split('?')[0]

  // Split the path to get the bucket and key
  const parts = path.split('/')
  const bucket = parts[0]
  const key = parts.slice(1).join('/')

  return { bucket, key }
}

export class Urls {
  public static bucketAndKeyToS3Url = (bucket: string, key: string) => {
    return `https://${bucket}.s3.us-west-2.amazonaws.com/${key}`
  }

  public static bucketAndKeyToR2Url = (bucket: string, key: string) => {
    return `https://ff764cfb9753fa688d8e5a5e4587bce6.r2.cloudflarestorage.com/${bucket}/${key}`
  }

  public static bucketAndKeyToUrl = (
    provider: UrlProviderLocation,
    bucket: string,
    key: string
  ) => {
    if (provider === UrlProviderLocation.R2) {
      return Urls.bucketAndKeyToR2Url(bucket, key)
    }
    return Urls.bucketAndKeyToS3Url(bucket, key)
  }

  public static isS3Url = (url: string) => {
    try {
      const parsedUrl = new URL(url)
      return (
        parsedUrl.hostname.endsWith('.amazonaws.com') &&
        (parsedUrl.hostname.includes('s3.') || parsedUrl.hostname.startsWith('s3-'))
      )
    } catch (error) {
      return false
    }
  }

  public static isR2Url = (url: string) => {
    try {
      const parsedUrl = new URL(url)
      return parsedUrl.hostname.endsWith('.r2.cloudflarestorage.com')
    } catch (error) {
      return false
    }
  }

  public static getUrlProvider = (url: string) => {
    if (this.isS3Url(url)) {
      return UrlProviderLocation.S3
    }

    if (this.isR2Url(url)) {
      return UrlProviderLocation.R2
    }

    return null
  }

  /** Given a cloud provider URL (S3, R2), this determines the provider and properly parses the bucket and key */
  public static parseUrlBucketAndKey(url: string) {
    if (this.isS3Url(url)) {
      return parseS3BucketAndKey(url)
    }

    if (this.isR2Url(url)) {
      return parseR2BucketAndKey(url)
    }

    throw new Error(`Invalid URL: ${url}`)
  }

  /**
   * Get file extension of a URL
   */
  public static getFileExtension(url: string) {
    const extensions = url.split('.')
    // remove query params
    const extension = extensions[extensions.length - 1].split('?')[0]
    return extension
  }
}
